<script lang="ts">
  import PhoneInput from "@shared/svelte/Components/PhoneInput.svelte";
  import ValidationError from "@shared/svelte/Components/ValidationError.svelte";
  import FormErrors from "@shared/svelte/Components/FormErrors.svelte";
  import type { Errors } from "@shared/types/Errors";
  import type { UserSignUp } from "@shared/types/User";
  import { form, bindClass } from "@shared/lib/svelte-forms";
  import SignupUserStore from "@onboarding2/svelte/Stores/SignupUserStore";
  import { onDestroy } from "svelte";

  export let email: string;
  export let onUserCreated: () => void;

  let user: UserSignUp = { phone: null, first_name: null, last_name: null, password: null };

  let submitting = false;
  let errors: Errors = {
    messages: [],
  };

  const signupUserStore = SignupUserStore(user);

  const minLength = val => {
    return {
      valid: val.length >= 8,
      name: "customMin",
    };
  };

  const containsSpecialChar = val => ({
    valid: /(?=.*[!@#$%^&*_])/.test(val),
    name: "specialChar",
  });
  const containsNumber = val => ({ valid: /\d/.test(val), name: "containsNumber" });

  const isValid = (p: string) => {
    if (p) {
      let phoneRe = /^[2-9]\d{2}[2-9]\d{2}\d{4}$/;
      let digits = p.replace(/\D/g, "");
      return phoneRe.test(digits);
    }
  };

  const phoneValidator = (val: string) => {
    return {
      valid: isValid(val) || val == "",
      name: "validPhone",
    };
  };

  const errorMessages = {
    customMin: value => `${value} length is invalid`,
    validPhone: value => `Phone number is invalid`,
    specialChar: value => `${value} requires at least one special character`,
    containsNumber: value => `${value} requires at least one number`,
  };

  const userOnboardingForm = form(
    () => ({
      first_name: { value: user.first_name, validators: ["required"] },
      last_name: { value: user.last_name, validators: ["required"] },
      password: {
        value: user.password,
        validators: ["required", minLength, containsSpecialChar, containsNumber],
      },
      phone: { value: user.phone, validators: ["required", phoneValidator] },
    }),
    {
      initCheck: false,
    }
  );

  const unsubscribeErrors = signupUserStore.errors.subscribe(n => {
    if (n) {
      if (n.messages[0] == "Email has already been taken") {
        n.messages[0] =
          "This email address is already in use. <a href='/users/sign_in?ref=1' onclick=\"window.location.href='/users/sign_in?ref=1';\">Login</a> or <a href='/start/passwordReset'>recover your password</a>";
      }
    }
    errors = n;
  });

  const runSubmitUserWrapper = () => {
    if (!$userOnboardingForm.valid) {
      userOnboardingForm.markDirty();
      return;
    }

    submitting = true;
    user.email = email;
    signupUserStore
      .save(user)
      .then((_res: any) => {
        onUserCreated();
      })
      .catch((_err: any) => {
        submitting = false;
      });
  };

  onDestroy(unsubscribeErrors);

</script>

<div class="fizz-layout-one-col-centered">
  <FormErrors {errors} />
  <!-- svelte-ignore component-name-lowercase -->
  <form
    on:submit|preventDefault={runSubmitUserWrapper}
    class="fizz-card fizz-flow-16 fizz-radius-medium"
  >
    <h2 class="fizz-heading-2 fizz-text-center fizz-stack-8">Sign up for Provi</h2>
    <p class="fizz-text-center fizz-text-medium fizz-stack-16">
      {email}
    </p>

    <div class="fizz-flex-row-responsive fizz-align-content-start">
      <div class="fizz-flex-item fizz-full-width">
        <label class="fizz-label fizz-stack-4" for="first_name">First Name</label>
        <input
          type="text"
          id="first_name"
          name="first_name"
          class="fizz-input-text fizz-full-width"
          bind:value={user.first_name}
          use:bindClass={{ form: userOnboardingForm }}
        />
        <ValidationError name="First Name" fieldObj={$userOnboardingForm.fields.first_name} />
      </div>

      <div class="fizz-flex-item fizz-full-width">
        <label class="fizz-label fizz-stack-4" for="last_name">Last Name</label>
        <input
          type="text"
          id="last_name"
          name="last_name"
          class="fizz-input-text fizz-full-width"
          bind:value={user.last_name}
          use:bindClass={{ form: userOnboardingForm }}
        />
        <ValidationError name="Last Name" fieldObj={$userOnboardingForm.fields.last_name} />
      </div>
    </div>

    <div>
      <label class="fizz-label fizz-stack-4" for="password"> Password </label>
      <input
        autocomplete="password"
        type="password"
        id="password"
        name="password"
        class="fizz-input-text fizz-full-width fizz-stack-4"
        bind:value={user.password}
        use:bindClass={{ form: userOnboardingForm }}
      />
      <ValidationError
        name="Password"
        fieldObj={$userOnboardingForm.fields.password}
        {errorMessages}
        extraClasses="fizz-stack-4"
      />
      <p class="fizz-text-subdued">
        Password must be at least 8 characters, contain letters, numbers and special characters (!,
        @, #, $, %, ^, &, *, _)
      </p>
    </div>

    <div>
      <label class="fizz-label fizz-stack-4" for="phone"> Mobile Phone Number </label>
      <PhoneInput
        extraClasses="fizz-input-text fizz-full-width fizz-stack-4"
        bind:value={user.phone}
        action={[bindClass, { form: userOnboardingForm }]}
        id="phone"
        name="phone"
        placeholder=""
      />
      <ValidationError
        name="Phone number"
        fieldObj={$userOnboardingForm.fields.phone}
        {errorMessages}
      />
      <p class="fizz-text-subdued">This number will be used for SMS order notifications</p>
    </div>

    <p class="fizz-text-center fizz-text-small fizz-stack-24">
      By creating your Provi account, you agree to our <a href="/terms">Terms of Service</a> and
      <a href="/privacy">Privacy Policy</a>
    </p>

    <button
      href="#"
      type="submit"
      class="fizz-button-secondary fizz-full-width fizz-button-large"
      disabled={submitting}
    >
      Create your account
    </button>
  </form>
</div>
